export default function PalmHarborDestinationsSection() {
  const stats = [
    {
      value: "42%",
      label: "of Palm Harbor requests are Caribbean cruise or resort travel with under 4 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for golf trips to Scotland, Ireland, and European destinations",
    },
    {
      value: "Dec–Apr",
      label: "peak snowbird and cruise season — passport renewals spike before embarkation",
    },
    {
      value: "48-hour",
      label: "shortest golf trip departure window we've accommodated for a Palm Harbor group",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Palm Harbor families and retirees — whether by cruise from Port Tampa Bay or Port Canaveral, or by direct flight from TPA to Nassau, Jamaica, or the Cayman Islands. All cruise passengers and most air travelers need valid passports. The Bahamas requires six months of remaining validity for entry.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Scotland & Ireland",
      context: "The most common golf and heritage destination for Palm Harbor retirees and golf enthusiasts. St. Andrews, Royal County Down, Ballybunion, and Trump Turnberry are bucket-list courses. Scotland and Ireland also attract heritage travelers tracing ancestry. These trips are often planned six to twelve months in advance but passport checks may happen much later.",
      urgency: "Golf group trips to Scotland are often booked 6–12 months ahead, but passport renewals may not be checked until months later — creating a timing gap",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for Palm Harbor families seeking beach vacations within a short flight. TPA offers connecting service through Atlanta, Charlotte, and Houston. Business travelers also fly to Mexico City for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Palm Harbor families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, Dublin, and Belfast draw Palm Harbor travelers for golf, heritage, cultural exploration, and family visits. TPA offers direct seasonal service to London and year-round connections through domestic hubs. The UK requires passports valid for the duration of your stay, but airlines may enforce stricter rules.",
      urgency: "Summer family trips to the UK are often planned 3–4 months in advance — insufficient time if passport renewal is needed",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Vancouver, and Montreal are popular destinations for Palm Harbor retirees visiting family, sports fans attending Blue Jays games or NHL matchups, and nature enthusiasts exploring the Canadian Rockies. TPA offers connecting flights through Detroit, Charlotte, and Chicago. Canada requires passports valid for the duration of your stay.",
      urgency: "Toronto sports trips and family visits are often planned with 2–4 weeks of notice — too short for standard processing if renewal is needed",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe — France, Spain, Italy, Germany",
      context: "Paris, Barcelona, Rome, and Munich draw Palm Harbor families for summer cultural vacations, food and wine tours, and multi-generational trips. These are typically the most planned trips but also the most expensive to cancel if a passport issue emerges. The Schengen Area requires six months of remaining validity.",
      urgency: "European summer trips are planned 4–6 months ahead, but children's five-year passports may expire during that window without parents noticing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Palm Harbor Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Palm Harbor
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Palm Harbor travelers head to the Caribbean for cruises and resorts, to Scotland and Ireland for golf, and to Europe and Latin America for family vacations. These are the destinations that create the most passport urgency for our Palm Harbor clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Tampa Bay and the Gulf Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Clearwater", href: "/clearwater" },
              { name: "Dunedin", href: "/dunedin" },
              { name: "St. Petersburg", href: "/st-petersburg" },
              { name: "Tampa", href: "/tampa" },
              { name: "Sarasota", href: "/sarasota" },
              { name: "Venice", href: "/venice" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}